import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

// This slice stores all detail rows in a single entity adapter, and
// maintains a per-item (swimlane/line-item) ordering of row IDs.

const rowsAdapter = createEntityAdapter({
  selectId: row => row.id,
});

const initialState = rowsAdapter.getInitialState({
  // Map of selected item id -> ordered array of row ids
  idsByItemId: {},
});

const detailsRowsSlice = createSlice({
  name: 'detailsRows',
  initialState,
  reducers: {
    setRowsForItem(state, action) {
      const { itemId, rows } = action.payload;
      rowsAdapter.upsertMany(state, rows || []);
      state.idsByItemId[itemId] = (rows || []).map(r => r.id);
    },
    upsertRowForItem(state, action) {
      const { itemId, row } = action.payload;
      if (!row?.id) return;
      rowsAdapter.upsertOne(state, row);
      const list = state.idsByItemId[itemId] || [];
      if (!list.includes(row.id)) {
        state.idsByItemId[itemId] = [...list, row.id];
      }
    },
    removeRowForItem(state, action) {
      const { itemId, rowId } = action.payload;
      rowsAdapter.removeOne(state, rowId);
      const list = state.idsByItemId[itemId] || [];
      state.idsByItemId[itemId] = list.filter(id => id !== rowId);
    },
    reorderRowsForItem(state, action) {
      const { itemId, orderedIds } = action.payload;
      state.idsByItemId[itemId] = orderedIds;
    },
    clearRowsForItem(state, action) {
      const { itemId } = action.payload;
      state.idsByItemId[itemId] = [];
    },
  },
});

export const {
  setRowsForItem,
  upsertRowForItem,
  removeRowForItem,
  reorderRowsForItem,
  clearRowsForItem,
} = detailsRowsSlice.actions;

export default detailsRowsSlice.reducer;

// Selectors
export const detailsRowsSelectors = rowsAdapter.getSelectors(
  state => state.detailsRows
);

export const selectRowIdsForItem = (state, itemId) =>
  state.detailsRows.idsByItemId[itemId] || [];

export const selectRowsForItem = (state, itemId) => {
  const { entities } = state.detailsRows;
  const ids = state.detailsRows.idsByItemId[itemId] || [];
  return ids.map(id => entities[id]).filter(Boolean);
};
